import ubicacionXUsuarioService from '../services/ubicacionXUsuarioService';

export interface UbicacionUsuarioDraft {
  nroUbicacion: number;
  nombreUbicacion: string;
  preferida: boolean;
}

export function mapAsignacionesToDraft(
  items: { nroUbicacion: number; nombreUbicacion?: string | null; preferida: boolean }[]
): UbicacionUsuarioDraft[] {
  return items.map((item) => ({
    nroUbicacion: item.nroUbicacion,
    nombreUbicacion: item.nombreUbicacion?.trim() || `Ubicación ${item.nroUbicacion}`,
    preferida: item.preferida,
  }));
}

export async function syncUbicacionesUsuario(
  usuario: string,
  originales: UbicacionUsuarioDraft[],
  draft: UbicacionUsuarioDraft[]
): Promise<void> {
  const usuarioTrim = usuario.trim();
  if (!usuarioTrim) return;

  const origMap = new Map(originales.map((o) => [o.nroUbicacion, o]));
  const draftMap = new Map(draft.map((d) => [d.nroUbicacion, d]));

  for (const orig of originales) {
    if (!draftMap.has(orig.nroUbicacion)) {
      await ubicacionXUsuarioService.delete(orig.nroUbicacion, usuarioTrim);
    }
  }

  for (const item of draft) {
    if (!origMap.has(item.nroUbicacion)) {
      await ubicacionXUsuarioService.create({
        usuario: usuarioTrim,
        nroUbicacion: item.nroUbicacion,
      });
    }
  }

  const preferidaDraft = draft.find((d) => d.preferida);
  const preferidaOrig = originales.find((o) => o.preferida);
  if (
    preferidaDraft &&
    Number(preferidaDraft.nroUbicacion) !== Number(preferidaOrig?.nroUbicacion)
  ) {
    await ubicacionXUsuarioService.setPreferida(Number(preferidaDraft.nroUbicacion), usuarioTrim);
  }
}

/** Admin: solo persiste la ubicación por defecto (0 o 1 registro, sin restricción de acceso). */
export async function syncUbicacionPredeterminadaAdmin(
  usuario: string,
  originales: UbicacionUsuarioDraft[],
  selected: UbicacionUsuarioDraft | null
): Promise<void> {
  const usuarioTrim = usuario.trim();
  if (!usuarioTrim) return;

  for (const orig of originales) {
    if (!selected || Number(orig.nroUbicacion) !== Number(selected.nroUbicacion)) {
      await ubicacionXUsuarioService.delete(Number(orig.nroUbicacion), usuarioTrim);
    }
  }

  if (!selected) return;

  const nroUbicacion = Number(selected.nroUbicacion);
  const existsInOrig = originales.some((o) => Number(o.nroUbicacion) === nroUbicacion);
  if (!existsInOrig) {
    await ubicacionXUsuarioService.create({
      usuario: usuarioTrim,
      nroUbicacion,
    });
  }

  // Siempre marcar preferida: el create solo la setea si era la primera asignación.
  await ubicacionXUsuarioService.setPreferida(nroUbicacion, usuarioTrim);
}

export function extractUbicacionPredeterminadaAdmin(
  items: { nroUbicacion: number; nombreUbicacion?: string | null; preferida: boolean }[]
): UbicacionUsuarioDraft | null {
  if (items.length === 0) return null;
  const preferida = items.find((i) => i.preferida) ?? items[0];
  return {
    nroUbicacion: preferida.nroUbicacion,
    nombreUbicacion: preferida.nombreUbicacion?.trim() || `Ubicación ${preferida.nroUbicacion}`,
    preferida: true,
  };
}
